import { memo, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ICostSummary, ICostByOperator } from "../../../interfaces/dashboard";
import {
  formatCurrency,
  formatCarrierName,
  currencyCodeFor,
} from "../../../utils/metricsFormatters";

interface ICostSummaryProps {
  data: ICostSummary;
  // Opcional: si viene, permite derivar "costo más bajo/alto por operador"
  // sin pedir un dato nuevo al backend (ver análisis). Si no viene (todavía
  // cargando, o falló su fetch), esas dos líneas simplemente no se muestran.
  costByOperator?: ICostByOperator[] | null;
  // País activo (interno). Define la moneda de todos los montos de la tarjeta.
  country?: string;
}

const cheapestAndPriciest = (rows: ICostByOperator[]) => {
  const cheapest = rows.reduce((min, row) => (row.avgCost < min.avgCost ? row : min));
  const priciest = rows.reduce((max, row) => (row.avgCost > max.avgCost ? row : max));
  return { cheapest, priciest };
};

// Línea simple valor/label, sin comparación vs. período anterior (omitida por
// definición de producto).
const CostLine = ({ label, value }: { label: string; value: string }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

const OperatorCostLine = ({ label, name, amount, country }: { label: string; name: string; amount: number; country?: string }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {name} · {formatCurrency(amount, country)}
    </Typography>
  </Box>
);

const CostSummary = ({ data, costByOperator, country }: ICostSummaryProps) => {
  // Memoizado: cheapestAndPriciest recorre el array con dos reduce en cada
  // render y no depende de nada más que costByOperator.
  const { cheapest, priciest } = useMemo(
    () =>
      costByOperator && costByOperator.length > 0
        ? cheapestAndPriciest(costByOperator)
        : { cheapest: null, priciest: null },
    [costByOperator],
  );

  return (
    <Box>
      {/* Figma: "div.text-3xl" = 30px, no el 34px del h4 por default. */}
      <Typography
        variant="h4"
        fontWeight={700}
        color="text.primary"
        sx={{ fontSize: "1.875rem" }}
      >
        {formatCurrency(data.totalCost, country)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {currencyCodeFor(country)} acumulado en el período
      </Typography>
      <CostLine
        label="Costo prom. por envío"
        value={formatCurrency(data.avgCostPerOrder, country)}
      />
      {cheapest && (
        <OperatorCostLine
          label="Costo más bajo"
          name={formatCarrierName(cheapest.carrier_name, cheapest.carrier_id)}
          amount={cheapest.avgCost}
          country={country}
        />
      )}
      {priciest && (
        <OperatorCostLine
          label="Costo más alto"
          name={formatCarrierName(priciest.carrier_name, priciest.carrier_id)}
          amount={priciest.avgCost}
          country={country}
        />
      )}
    </Box>
  );
};

export default memo(CostSummary);
